/**
 * BigSplice Upload Helper Component
 * Modal for guiding users through upload options
 */

import React, { useState, useCallback } from 'react';
import { DownloadProgress } from '../../hooks/useChatState';
import { lambdaService } from '../../services/lambdaService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Upload,
  Link2,
  Database,
  Cloud,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface UploadHelperProps {
  isOpen: boolean;
  method: 'direct' | 'url' | 's3' | 'sql' | null;
  downloadProgress: DownloadProgress | null;
  onClose: () => void;
  onSelectMethod: (method: 'direct' | 'url' | 's3' | 'sql') => void;
  onUpdateProgress: (progress: DownloadProgress | null) => void;
}

export const UploadHelper: React.FC<UploadHelperProps> = ({
  isOpen,
  method,
  downloadProgress,
  onClose,
  onSelectMethod,
  onUpdateProgress,
}) => {
  const [url, setUrl] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    onSelectMethod(null as any);
    setUrl('');
    setError(null);
    onUpdateProgress(null);
  };

  const handleUrlDownload = useCallback(async () => {
    if (!url.trim()) return;

    setIsDownloading(true);
    setError(null);

    try {
      // Start the download
      const response = await lambdaService.downloadFromUrl(url);

      if (!response.success) {
        throw new Error(response.error || 'Failed to start download');
      }

      // If there's a download_id, we can poll for progress
      if (response.download_id) {
        const downloadId = response.download_id;

        // Poll for progress
        const pollProgress = async () => {
          try {
            const progressResponse = await lambdaService.getDownloadProgress(downloadId);

            const statusMap: Record<string, 'pending' | 'downloading' | 'processing' | 'completed' | 'failed'> = {
              'starting': 'pending',
              'downloading': 'downloading',
              'processing': 'processing',
              'completed': 'completed',
              'failed': 'failed',
              'not_found': 'failed',
            };

            onUpdateProgress({
              status: statusMap[progressResponse.status || 'pending'] || 'pending',
              percentage: progressResponse.progress || 0,
              speed: progressResponse.speed_mbps ? `${progressResponse.speed_mbps.toFixed(1)} MB/s` : undefined,
              eta: progressResponse.eta_seconds ? `${Math.ceil(progressResponse.eta_seconds)}s` : undefined,
              filename: progressResponse.filename,
              error: progressResponse.error,
            });

            if (progressResponse.status === 'completed') {
              setIsDownloading(false);
              // Close after a short delay to show success
              setTimeout(() => {
                onClose();
              }, 2000);
            } else if (progressResponse.status === 'failed') {
              setIsDownloading(false);
              setError(progressResponse.error || 'Download failed');
            } else {
              // Continue polling
              setTimeout(pollProgress, 2000);
            }
          } catch (pollError) {
            setIsDownloading(false);
            setError(
              pollError instanceof Error
                ? pollError.message
                : 'Failed to track download progress'
            );
          }
        };

        // Start polling
        onUpdateProgress({
          status: 'pending',
          percentage: 0,
        });
        pollProgress();
      } else {
        // No download_id - download started but we can't track progress
        // Show success and close after delay
        onUpdateProgress({
          status: 'processing',
          percentage: 100,
          filename: response.filename,
        });
        setIsDownloading(false);

        // Close after showing success message
        setTimeout(() => {
          onUpdateProgress({
            status: 'completed',
            percentage: 100,
            filename: response.filename,
          });
          setTimeout(() => {
            onClose();
          }, 2000);
        }, 500);
      }
    } catch (err) {
      setIsDownloading(false);
      setError(
        err instanceof Error ? err.message : 'Failed to start download'
      );
    }
  }, [url, onUpdateProgress, onClose]);

  const uploadMethods = [
    {
      id: 'direct' as const,
      icon: Upload,
      title: 'Direct Upload',
      description: 'Drag & drop or browse files (up to 5GB)',
      supported: 'CSV, Parquet, Excel, JSON, ZIP',
    },
    {
      id: 'url' as const,
      icon: Link2,
      title: 'URL Download',
      description: 'Download from a web link',
      supported: 'Dropbox, Google Drive, direct links',
    },
    {
      id: 's3' as const,
      icon: Cloud,
      title: 'S3 Transfer',
      description: 'Transfer from your AWS S3 bucket',
      supported: 'Requires AWS credentials',
    },
    {
      id: 'sql' as const,
      icon: Database,
      title: 'SQL Database',
      description: 'Import from a database',
      supported: 'MySQL, PostgreSQL, MariaDB',
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {method && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 mr-1"
                onClick={handleBack}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <Upload className="h-5 w-5" />
            {method ? uploadMethods.find((m) => m.id === method)?.title : 'Upload Data'}
          </DialogTitle>
          <DialogDescription>
            {method
              ? uploadMethods.find((m) => m.id === method)?.description
              : 'Choose how you want to add data to BigSplice'}
          </DialogDescription>
        </DialogHeader>

        {/* Method Selection */}
        {!method && (
          <div className="grid grid-cols-2 gap-3 py-4">
            {uploadMethods.map((uploadMethod) => (
              <Card
                key={uploadMethod.id}
                className={cn(
                  'cursor-pointer transition-all hover:border-primary hover:shadow-sm',
                  'border-2'
                )}
                onClick={() => onSelectMethod(uploadMethod.id)}
              >
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <uploadMethod.icon className="h-4 w-4 text-primary" />
                    </div>
                    <CardTitle className="text-sm">
                      {uploadMethod.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <CardDescription className="text-xs">
                    {uploadMethod.supported}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Direct Upload */}
        {method === 'direct' && (
          <div className="py-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm font-medium mb-1">
                Drag & drop files here
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                or click to browse
              </p>
              <Button variant="outline" size="sm">
                Choose Files
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Supported: CSV, Parquet, Excel, JSON, ZIP | Max 5GB per file
            </p>
          </div>
        )}

        {/* URL Download */}
        {method === 'url' && (
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">File URL</label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://dropbox.com/s/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={isDownloading}
                />
                <Button
                  onClick={handleUrlDownload}
                  disabled={!url.trim() || isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Download Progress */}
            {downloadProgress && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {downloadProgress.status === 'completed' ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : downloadProgress.status === 'failed' ? (
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <span className="text-sm font-medium">
                    {downloadProgress.status === 'completed'
                      ? 'Download complete!'
                      : downloadProgress.status === 'failed'
                      ? 'Download failed'
                      : 'Downloading...'}
                  </span>
                </div>

                {downloadProgress.status !== 'failed' && (
                  <>
                    <Progress value={downloadProgress.percentage} />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{downloadProgress.percentage}%</span>
                      {downloadProgress.speed && (
                        <span>{downloadProgress.speed}</span>
                      )}
                      {downloadProgress.eta && (
                        <span>ETA: {downloadProgress.eta}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="flex items-start gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Supports Dropbox, Google Drive (public links), and direct file URLs
            </p>
          </div>
        )}

        {/* S3 Transfer */}
        {method === 's3' && (
          <div className="py-4 text-center">
            <Cloud className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm mb-4">
              Transfer files from your AWS S3 bucket
            </p>
            <Button onClick={() => window.location.href = '/upload'}>
              Configure S3 Connection
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              You'll need your AWS Access Key and Secret Key
            </p>
          </div>
        )}

        {/* SQL Import */}
        {method === 'sql' && (
          <div className="py-4 text-center">
            <Database className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm mb-4">
              Import data from a SQL database
            </p>
            <Button onClick={() => window.location.href = '/upload'}>
              Configure Database Connection
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              Supports MySQL, PostgreSQL, MariaDB, and more
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadHelper;
